import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import IdentityScannerBox from "@/components/IdentityScannerBox";

const empty = { nom: "", telephone: "", email: "", adresse: "", notes: "" };
const emptyAddr = { rue: "", code_postal: "", localite: "", delegation: "", gouvernorat: "", full: "" };

export default function NewClientDialog({ onCreated, triggerLabel = "Nouveau client", testId = "new-client-trigger" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [addr, setAddr] = useState(emptyAddr);
  const [saving, setSaving] = useState(false);
  const formRef = useRef(null);

  const submit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (formRef.current && !formRef.current.checkValidity()) {
      formRef.current.reportValidity();
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, adresse: addr.full || form.adresse };
      const { data } = await api.post("/clients", payload);
      toast.success("Client créé");
      setOpen(false);
      setForm(empty);
      setAddr(emptyAddr);
      onCreated?.(data);
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur lors de la création du client");
    } finally {
      setSaving(false);
    }
  };

  const applyIdentity = (d) => {
    const pieceParts = [];
    if (d.type_piece) pieceParts.push(d.type_piece);
    if (d.numero_piece) pieceParts.push(`N° ${d.numero_piece}`);
    if (d.date_naissance) pieceParts.push(`Né(e) le ${d.date_naissance}`);
    if (d.lieu_naissance) pieceParts.push(`à ${d.lieu_naissance}`);
    if (d.date_expiration) pieceParts.push(`Expire ${d.date_expiration}`);
    const pieceLine = pieceParts.length ? pieceParts.join(" · ") : "";

    setForm((f) => ({
      ...f,
      nom: d.nom_complet || f.nom,
      telephone: d.telephone || f.telephone,
      email: d.email || f.email,
      notes: pieceLine
        ? (f.notes ? `${f.notes}\n${pieceLine}` : pieceLine)
        : f.notes,
    }));
    if (d.adresse) {
      setAddr((a) => {
        const next = { ...a, rue: d.adresse };
        next.full = [next.rue, next.localite, `${next.code_postal} ${next.delegation}`.trim(), next.gouvernorat]
          .filter((s) => s && s !== " ")
          .join(" · ");
        return next;
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={testId}
          className="rounded-sm whitespace-nowrap"
        >
          <UserPlus size={14} className="mr-1" /> {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Nouveau client</DialogTitle>
        </DialogHeader>

        <IdentityScannerBox onExtract={applyIdentity} testIdPrefix="newclient-id-scan" compact />

        <form ref={formRef} onSubmit={submit} className="space-y-3" onClick={(e) => e.stopPropagation()}>
          <div>
            <Label className="text-xs uppercase tracking-wider">Nom *</Label>
            <Input data-testid="newclient-nom" required autoFocus value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} className="mt-1 rounded-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Téléphone *</Label>
              <Input data-testid="newclient-tel" required value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="Ex: 0612345678" className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <Input data-testid="newclient-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Adresse</Label>
            <div className="mt-1">
              <AddressAutocomplete value={addr} onChange={setAddr} dataTestid="newclient-addr" />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              Tapez une ville, un code postal ou une adresse — vous pouvez aussi laisser vide.
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Notes</Label>
            <Textarea data-testid="newclient-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 rounded-sm" />
          </div>
          <DialogFooter>
            <Button
              type="button"
              disabled={saving}
              data-testid="newclient-submit"
              onClick={(e) => { e.stopPropagation(); submit(e); }}
              className="bg-klein hover:bg-blue-800 text-white rounded-sm"
            >
              {saving ? "Création…" : "Créer et sélectionner"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
